import { FileText, Calendar, User, ArrowLeft, Building2, TrendingUp, DollarSign } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import AuthenticatedLayout from '../components/AuthenticatedLayout'
import { selfAssessmentService } from '../utils/selfAssessmentService'

export default function AssessmentDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [assessment, setAssessment] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (id) {
      fetchAssessment()
    }
  }, [id])

  const fetchAssessment = async () => {
    if (!id) return

    try {
      setIsLoading(true)
      setError(null)
      
      // First, get all assessments to find the one with matching ID
      const response = await selfAssessmentService.getAllSelfAssessments()

      if (response.statusCode === 200 && response.response) {
        const data: any = response.response
        let assessmentsArray: any[] = []
        
        if (Array.isArray(data)) {
          assessmentsArray = data
        } else if (typeof data === 'object') {
          if (data.success && data.data) {
            assessmentsArray = Array.isArray(data.data) ? data.data : []
          } else {
            assessmentsArray = data.assessments || data.data || data.items || data.docs || []
          }
        }
        
        // Find the assessment with matching ID
        const foundAssessment = assessmentsArray.find((a: any) => a._id === id)
        
        if (foundAssessment) {
          setAssessment(foundAssessment)
        } else {
          setError('Assessment not found')
        }
      }
    } catch (err: any) {
      console.error('Error fetching assessment:', err)
      setError('Failed to load assessment details')
    } finally {
      setIsLoading(false)
    }
  }

  const formatCurrency = (value: number | undefined | null): string => {
    if (value === undefined || value === null) return '£0.00'
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(value)
  }

  const formatDate = (dateString: string | undefined | null): string => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    })
  }

  const getStatusBadge = () => {
    if (!assessment) return null
    
    let status = 'Draft'
    let bgColor = 'bg-gray-100 text-gray-800'
    
    if (assessment.calculation?.payload?.finalDeclaration) {
      status = 'Submitted'
      bgColor = 'bg-green-100 text-green-800'
    } else if (assessment.selfEmployment && assessment.selfEmployment.length > 0) {
      status = 'In Review'
      bgColor = 'bg-blue-100 text-blue-800'
    } else {
      status = 'New'
      bgColor = 'bg-yellow-100 text-yellow-800'
    }
    
    return (
      <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${bgColor}`}>
        {status}
      </span>
    )
  }

  return (
    <AuthenticatedLayout activeTab='filings'>
      <div className='grid grid-cols-1 gap-6'>
        {/* Back Button */}
        <button
          onClick={() => navigate('/history')}
          className='flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors w-fit'
        >
          <ArrowLeft className='w-4 h-4' />
          <span>Back to Assessment History</span>
        </button>

        {isLoading ? (
          <div className='flex items-center justify-center py-12'>
            <div className='text-center'>
              <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto'></div>
              <p className='mt-4 text-gray-600'>Loading assessment details...</p>
            </div>
          </div>
        ) : error ? (
          <div className='bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm'>
            {error}
          </div>
        ) : assessment ? (
          <>
            {/* Assessment Header */}
            <div className='p-6 bg-white rounded-2xl shadow-sm border-0'>
              <div className='flex items-start justify-between mb-6'>
                <div className='flex items-start gap-4'>
                  <div className='p-3 bg-[#001B38] rounded-lg'>
                    <FileText className='w-8 h-8 text-white' />
                  </div>
                  <div>
                    <h1 className='text-2xl font-bold text-gray-900 mb-1'>
                      Self Assessment
                    </h1>
                    <p className='text-gray-600'>
                      Tax Year: {assessment.taxYear || 'N/A'}
                    </p>
                    {getStatusBadge()}
                  </div>
                </div>
              </div>

              {/* Basic Information */}
              <div className='grid grid-cols-1 md:grid-cols-2 gap-8 mt-6'>
                <div className='space-y-6 text-start'>
                  <div>
                    <h3 className='text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2'>
                      <User className='w-4 h-4' />
                      Basic Information
                    </h3>
                    <div className='space-y-3 text-start'>
                      <div>
                        <p className='text-sm text-gray-500'>NINO</p>
                        <p className='text-sm font-medium text-gray-900'>
                          {assessment.nino || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className='text-sm text-gray-500'>Tax Year</p>
                        <p className='text-sm font-medium text-gray-900'>
                          {assessment.taxYear || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className='text-sm text-gray-500'>Assessment ID</p>
                        <p className='text-sm font-medium text-gray-900 font-mono text-xs'>
                          {assessment._id || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Dates */}
                  <div>
                    <h3 className='text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2'>
                      <Calendar className='w-4 h-4' />
                      Dates
                    </h3>
                    <div className='space-y-3'>
                      <div>
                        <p className='text-sm text-gray-500'>Created At</p>
                        <p className='text-sm font-medium text-gray-900'>
                          {formatDate(assessment.createdAt)}
                        </p>
                      </div>
                      <div>
                        <p className='text-sm text-gray-500'>Last Updated</p>
                        <p className='text-sm font-medium text-gray-900'>
                          {formatDate(assessment.updatedAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Summary Stats */}
                <div>
                  <h3 className='text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2'>
                    <TrendingUp className='w-4 h-4' />
                    Summary
                  </h3>
                  <div className='space-y-3'>
                    <div className='p-4 bg-gray-50 rounded-lg'>
                      <p className='text-sm text-gray-500 mb-1'>Self-Employment Periods</p>
                      <p className='text-2xl font-bold text-gray-900'>
                        {assessment.selfEmployment?.length || 0}
                      </p>
                    </div>
                    <div className='p-4 bg-gray-50 rounded-lg'>
                      <p className='text-sm text-gray-500 mb-1'>Property Businesses</p>
                      <p className='text-2xl font-bold text-gray-900'>
                        {assessment.propertyBusiness?.length || 0}
                      </p>
                    </div>
                    <div className='p-4 bg-gray-50 rounded-lg'>
                      <p className='text-sm text-gray-500 mb-1'>Losses</p>
                      <p className='text-2xl font-bold text-gray-900'>
                        {assessment.losses?.length || 0}
                      </p>
                    </div>
                    {assessment.calculation && (
                      <div className='p-4 bg-green-50 rounded-lg border border-green-200'>
                        <p className='text-sm text-green-700 mb-1'>Final Declaration</p>
                        <p className='text-sm font-medium text-green-900'>
                          {assessment.calculation.payload?.finalDeclaration ? 'Yes' : 'No'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Self-Employment Periods */}
            {assessment.selfEmployment && assessment.selfEmployment.length > 0 && (
              <div className='p-6 bg-white rounded-2xl shadow-sm border-0'>
                <h3 className='text-lg font-bold text-gray-900 mb-6 flex items-center gap-2'>
                  <Building2 className='w-5 h-5' />
                  Self-Employment Periods
                </h3>
                <div className='space-y-6'>
                  {assessment.selfEmployment.map((period: any, index: number) => (
                    <div key={index} className='border border-gray-200 rounded-lg p-4'>
                      <div className='flex items-start justify-between mb-4'>
                        <div>
                          <h4 className='font-semibold text-gray-900'>
                            Period {index + 1}
                          </h4>
                          <p className='text-sm text-gray-500 mt-1'>
                            Business ID: {period.businessId || 'N/A'}
                          </p>
                          {period.payload?.periodDates && (
                            <p className='text-sm text-gray-600 mt-1'>
                              {formatDate(period.payload.periodDates.periodStartDate)} - {formatDate(period.payload.periodDates.periodEndDate)}
                            </p>
                          )}
                        </div>
                      </div>

                      {period.payload?.periodIncome && (
                        <div className='mt-4 p-4 bg-blue-50 rounded-lg'>
                          <h5 className='text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2'>
                            <DollarSign className='w-4 h-4' />
                            Income
                          </h5>
                          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                            <div>
                              <p className='text-xs text-gray-500'>Turnover</p>
                              <p className='text-sm font-medium text-gray-900'>
                                {formatCurrency(period.payload.periodIncome.turnover)}
                              </p>
                            </div>
                            <div>
                              <p className='text-xs text-gray-500'>Other Income</p>
                              <p className='text-sm font-medium text-gray-900'>
                                {formatCurrency(period.payload.periodIncome.other)}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {period.payload?.periodExpenses && (
                        <div className='mt-4 p-4 bg-orange-50 rounded-lg'>
                          <h5 className='text-sm font-semibold text-gray-900 mb-3'>Expenses</h5>
                          <div className='grid grid-cols-2 md:grid-cols-3 gap-4'>
                            {Object.entries(period.payload.periodExpenses).map(([key, value]: [string, any]) => (
                              <div key={key}>
                                <p className='text-xs text-gray-500 capitalize'>
                                  {key.replace(/([A-Z])/g, ' $1').trim()}
                                </p>
                                <p className='text-sm font-medium text-gray-900'>
                                  {formatCurrency(value)}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {period.createdAt && (
                        <p className='text-xs text-gray-400 mt-4'>
                          Created: {formatDate(period.createdAt)}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Property Business */}
            {assessment.propertyBusiness && assessment.propertyBusiness.length > 0 && (
              <div className='p-6 bg-white rounded-2xl shadow-sm border-0'>
                <h3 className='text-lg font-bold text-gray-900 mb-6'>Property Business</h3>
                <div className='space-y-4'>
                  {assessment.propertyBusiness.map((property: any, index: number) => (
                    <div key={index} className='border border-gray-200 rounded-lg p-4'>
                      <p className='text-sm text-gray-500'>Property {index + 1}</p>
                      <pre className='text-xs text-gray-600 mt-2 overflow-auto'>
                        {JSON.stringify(property, null, 2)}
                      </pre>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Losses */}
            {assessment.losses && assessment.losses.length > 0 && (
              <div className='p-6 bg-white rounded-2xl shadow-sm border-0'>
                <h3 className='text-lg font-bold text-gray-900 mb-6'>Losses</h3>
                <div className='space-y-4'>
                  {assessment.losses.map((loss: any, index: number) => (
                    <div key={index} className='border border-gray-200 rounded-lg p-4'>
                      <p className='text-sm text-gray-500'>Loss {index + 1}</p>
                      <pre className='text-xs text-gray-600 mt-2 overflow-auto'>
                        {JSON.stringify(loss, null, 2)}
                      </pre>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Calculation Info */}
            {assessment.calculation && (
              <div className='p-6 bg-white rounded-2xl shadow-sm border-0'>
                <h3 className='text-lg font-bold text-gray-900 mb-6'>Calculation</h3>
                <div className='border border-gray-200 rounded-lg p-4'>
                  <div className='space-y-2'>
                    <div>
                      <p className='text-sm text-gray-500'>Calculation ID</p>
                      <p className='text-sm font-medium text-gray-900 font-mono text-xs'>
                        {assessment.calculation.meta?.calculationId || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className='text-sm text-gray-500'>Final Declaration</p>
                      <p className='text-sm font-medium text-gray-900'>
                        {assessment.calculation.payload?.finalDeclaration ? 'Yes' : 'No'}
                      </p>
                    </div>
                    {assessment.calculation.updatedAt && (
                      <div>
                        <p className='text-sm text-gray-500'>Last Updated</p>
                        <p className='text-sm font-medium text-gray-900'>
                          {formatDate(assessment.calculation.updatedAt)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className='text-center py-12'>
            <p className='text-gray-500'>Assessment not found</p>
          </div>
        )}
      </div>
    </AuthenticatedLayout>
  )
}

